import { Selection, EnterElement, ScaleTime } from "d3"
import { debounce } from "lodash"
import { ReactCallbacks } from "../../../Types/ReactCallbacks"
import { D3OneToOneRenderable } from "../D3OneToOneRenderable"
import { formatDate } from "../../../utils"

export type D3TimelineTooltipConfig = {
	viewScale: ScaleTime<any, any, any>
	fileScale: ScaleTime<any, any, any>
	timeZone: string
	liveModeEnabled: boolean
}

export class D3TimelineTooltip extends D3OneToOneRenderable<SVGGElement, SVGGElement, D3TimelineTooltipConfig> {
	private tooltipSelection?: Selection<any, any, any, any>
	private width: number = 300
	private height: number = 20
	private context: CanvasRenderingContext2D | undefined = undefined
	private superSamplingRatio: number = 2 // supersampling avoids blurry text when the canvas is placed on a subpixel.
	private yOffset = -5


	constructor(root: SVGGElement, config: D3TimelineTooltipConfig, reactCallbacks: ReactCallbacks<any>) {
		super(root, config, "d3-timeline-tooltip", reactCallbacks)
		this.render()
	}

	public moveTo(date: Date) {
		const middlePosition = this.config.fileScale(date)
		this.renderText(this.getStartEndDateString())

		requestAnimationFrame(() => {
			if (this.tooltipSelection) {
				this.tooltipSelection
					.attr("transform", `translate(${Math.round(middlePosition - (this.width/2))}, ${-this.height + this.yOffset})`)
					.style("opacity", 1)
			}
		})
	}

	public setText(text: string) {
		this.renderText(text)
	}

	public cancelTooltipHide() {
		this.hideTimelineTooltip.cancel()
	}

	public hide(milliseconds: number) {
		const hideFunction = this.getDebouncedHide(milliseconds)
		if (this.tooltipSelection) {
			hideFunction(this.tooltipSelection)
		}
	}

	protected enter(newElements: Selection<EnterElement, D3TimelineTooltipConfig, any, any>): Selection<SVGGElement, D3TimelineTooltipConfig, SVGGElement, any> {
		this.tooltipSelection = newElements
			.append("g")
			.attr("class", this.className)
			.attr("transform", `translate(${Math.round(-this.width/2)}, ${-this.height + this.yOffset})`)
			.style("opacity", 0)
			.style("transition", "opacity 0.2s ease-in-out")
			.style("position", "absolyte")
			.style("top", 0)
			.style("left", 0)

		const foreignObject = this.tooltipSelection.append("foreignObject")
		const canvas = foreignObject.append("xhtml:canvas") as Selection<HTMLCanvasElement, any, any, any>

		foreignObject
			.attr("width", this.width)
			.attr("height", this.height)
			.style("width", `${this.width}px`)
			.style("height", `${this.height}px`)

		canvas
			.attr("width", this.width * this.superSamplingRatio)
			.attr("height", this.height * this.superSamplingRatio)
			.style("width", `${this.width}px`)
			.style("height", `${this.height}px`)

		const canvasNode = canvas.node()

		if (canvasNode != null) {
			this.context = canvasNode.getContext("2d") ?? undefined

			if (this.context) {
				this.context.font = `${0.8 * this.superSamplingRatio}em sans-serif`
				this.context.textAlign = "center"
				this.context.textBaseline = "bottom"
				this.context.imageSmoothingEnabled = false
			}
 		}

		this.renderText(this.getStartEndDateString())

 		return this.tooltipSelection
	}

	protected update(updatedElements: Selection<any, any, any, any>) {
		const [minX, maxX] = this.config.viewScale.range()
		const averageDate = this.config.viewScale.invert((minX + maxX) / 2)
		this.moveTo(averageDate)
		this.hideTimelineTooltip(updatedElements)

		return updatedElements
	}

	private getDebouncedHide = (milliseconds: number) => debounce((updatedElements: Selection<any, any, any, any>) => updatedElements.style("opacity", 0), milliseconds)

	private hideTimelineTooltip = this.getDebouncedHide(1000)

	private getStartEndDateString(): string {
		const [startDate, endDate] = this.config.viewScale.domain()

		const formattedStart = formatDate(startDate, { timeZone: this.config.timeZone, seconds: true })
		const formattedEnd = formatDate(endDate, { timeOnly: true, timeZone: this.config.timeZone, timeZoneName: true, seconds: true })

		return `${formattedStart} - ${formattedEnd}`
	}

	private renderText(text: string) {
		// The reason we're using a canvas instead of SVG is because SVG text elements cannot be updated without re-creating the DOM node.
		// And that happens EVERY FRAME. Not only is that memory intensive, it causes a bunch of garbage collection and frame drops.
		// With Canvas, no DOM updates are happening, which is much nicer on the CPU and prevents DOM node garbage collection.

		if (!this.context) {
			return
		}

		this.context.fillStyle = getComputedStyle(document.documentElement).getPropertyValue("--color-chart-axis-text").trim() || "black"
		this.context.clearRect(0, 0, this.width*this.superSamplingRatio, this.height*this.superSamplingRatio)
		this.context.fillText(text, Math.floor(this.width), this.height*this.superSamplingRatio)
	}
}
